import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, Grid, Header, Icon, Button, Menu, Label } from 'semantic-ui-react';
import ConversationList from './ConversationList';
import ConversationView from '../Conversation/ConversationView';
import NotificationList from './NotificationList';
import {
  fetchUserConversations,
  setActiveConversation,
  selectConversations,
  selectCurrentConversation,
  selectMessagesLoading,
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  selectNotifications,
  selectNotificationUnreadCount,
  selectNotificationsLoading
} from '../../../slices/messageSlice';
import { selectCurrentUser } from '../../../slices/userSlice';
import './MessageCenter.css';

const MessageCenter = ({ isOpen, onClose, initialConversationId = null, initialThreadId = null }) => {
  const dispatch = useDispatch();
  const conversations = useSelector(selectConversations);
  const currentConversation = useSelector(selectCurrentConversation);
  const loading = useSelector(selectMessagesLoading);
  const currentUser = useSelector(selectCurrentUser);
  const notifications = useSelector(selectNotifications);
  const notificationUnreadCount = useSelector(selectNotificationUnreadCount);
  const notificationsLoading = useSelector(selectNotificationsLoading);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('conversations');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && currentUser?.userId) {
      dispatch(fetchUserConversations(currentUser.userId));
      // Also fetch notifications when opening
      dispatch(fetchNotifications(currentUser.userId));
    }
  }, [isOpen, currentUser?.userId, dispatch]);

  useEffect(() => {
    if (initialConversationId && conversations.length > 0) {
      const conversation = conversations.find(conv => conv.conversationId === initialConversationId);
      if (conversation) {
        dispatch(setActiveConversation({
          ...conversation,
          activeThreadId: initialThreadId
        }));
      }
    }
  }, [initialConversationId, initialThreadId, conversations, dispatch]);

  const handleConversationSelect = (conversation, threadId = null) => {
    dispatch(setActiveConversation({
      ...conversation,
      activeThreadId: threadId
    }));
  };

  const handleBackToList = () => {
    dispatch(setActiveConversation(null));
  };

  // Notification handlers
  const handleMarkNotificationRead = (notificationId) => {
    dispatch(markNotificationRead(notificationId));
  };

  const handleMarkAllNotificationsRead = () => {
    if (currentUser?.userId) {
      dispatch(markAllNotificationsRead(currentUser.userId));
    }
  };

  // Calculate conversation unread count
  const conversationUnreadCount = (conversations || []).reduce(
    (sum, conv) => sum + (conv.unreadCount || 0), 0
  );

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size={isMobile ? 'fullscreen' : 'large'}
      className="message-center-modal"
    >
      <Modal.Header>
        <Icon name="comments" />
        Messages & Notifications
        <Button
          floated="right"
          icon="close"
          onClick={onClose}
          basic
          circular
        />
      </Modal.Header>

      <Modal.Content className="message-center-content" style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Tab Navigation - sticky at top */}
        <Menu pointing secondary style={{ marginBottom: 0, flexShrink: 0, padding: '0 14px', borderBottom: '1px solid #ddd' }}>
          <Menu.Item
            active={activeTab === 'conversations'}
            onClick={() => setActiveTab('conversations')}
          >
            <Icon name="comments" />
            Conversations
            {conversationUnreadCount > 0 && (
              <Label circular color="blue" size="mini" style={{ marginLeft: '8px' }}>
                {conversationUnreadCount > 99 ? '99+' : conversationUnreadCount}
              </Label>
            )}
          </Menu.Item>
          <Menu.Item
            active={activeTab === 'notifications'}
            onClick={() => setActiveTab('notifications')}
          >
            <Icon name="bell" />
            Notifications
            {notificationUnreadCount > 0 && (
              <Label circular color="orange" size="mini" style={{ marginLeft: '8px' }}>
                {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
              </Label>
            )}
          </Menu.Item>
        </Menu>

        {/* Scrollable tab content area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '14px' }}>
          {/* Conversations Tab */}
          {activeTab === 'conversations' && (
            <>
              {isMobile ? (
                // Mobile: Single column with navigation
                <div className="mobile-message-center">
                  {!currentConversation ? (
                    <ConversationList
                      conversations={conversations}
                      onConversationSelect={handleConversationSelect}
                      loading={loading.conversations}
                    />
                  ) : (
                    <ConversationView
                      conversation={currentConversation}
                      onBack={handleBackToList}
                      showBackButton={true}
                    />
                  )}
                </div>
              ) : (
                // Desktop: Two column layout
                <Grid className="desktop-message-center">
                  <Grid.Column width={5} className="conversation-list-column">
                    <ConversationList
                      conversations={conversations}
                      onConversationSelect={handleConversationSelect}
                      selectedConversationId={currentConversation?.conversationId}
                      loading={loading.conversations}
                    />
                  </Grid.Column>

                  <Grid.Column width={11} className="conversation-view-column">
                    {currentConversation ? (
                      <ConversationView
                        conversation={currentConversation}
                        showBackButton={false}
                      />
                    ) : (
                      <div className="no-conversation-selected">
                        <Icon name="comments outline" size="huge" />
                        <Header as="h3">Select a conversation to start messaging</Header>
                      </div>
                    )}
                  </Grid.Column>
                </Grid>
              )}
            </>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <NotificationList
              notifications={notifications}
              loading={notificationsLoading}
              onMarkRead={handleMarkNotificationRead}
              onMarkAllRead={handleMarkAllNotificationsRead}
              onClose={onClose}
            />
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default MessageCenter;
